import type { EquipmentAccessoryData } from 'components/accessories-page/accessory-type-panel';
import { usesQuantitySelection } from 'utils/accessory-type-presentation';
import {
  getLineSetSettingsForEquipment,
  isAccessoryIncludedWithLineSet
} from 'utils/include-recommended-accessories';
import type {
  AccessoryOptionGroupBuildOptionsType,
  AccessoryOptionGroupType,
  AccessoryOptionRecommendationType,
  AccessoryOptionType,
  AccessoryPanelLikeType,
  AccessoryQuantityChangeType,
  AccessorySelectionChangeType
} from 'utils/accessory-option-groups-types';

const ACCESSORY_TYPE_LABEL_OVERRIDES: Record<string, string> = {
  wifi: 'WiFi Adapter',
  plenum: 'Plenum Kit',
  brass_nut_union: 'Brass Nut / Union'
};

const EXCLUSIVE_ACCESSORY_CATEGORIES = new Set(['condenser_mounting']);

const recommendationSortRank = (priority: number): number =>
  priority > 0 ? priority : Number.POSITIVE_INFINITY;

const recommendationTypeFor = (
  priority: number
): AccessoryOptionRecommendationType => {
  if (priority === 1) {
    return 'recommended';
  }
  if (priority === 2) {
    return 'alternative';
  }
  return null;
};

const selectedQuantity = (
  item: EquipmentAccessoryData,
  productId: number
): number =>
  item.selectedAccessories.find((selected) => selected.productId === productId)
    ?.quantity ?? 0;

const toOption = (
  item: EquipmentAccessoryData,
  recommendation: EquipmentAccessoryData['recommendations'][number],
  product: AccessoryOptionType['product']
): AccessoryOptionType => ({
  product,
  recommendation,
  isRecommended: recommendation.recommendedPriority === 1,
  recommendationType: recommendationTypeFor(recommendation.recommendedPriority),
  quantity: selectedQuantity(item, product.productId)
});

const byRecommendationPriority = (
  left: AccessoryOptionType,
  right: AccessoryOptionType
): number =>
  recommendationSortRank(left.recommendation.recommendedPriority) -
  recommendationSortRank(right.recommendation.recommendedPriority);

const optionsForItem = (item: EquipmentAccessoryData): AccessoryOptionType[] =>
  item.recommendations.flatMap((recommendation) => {
    const product = item.accessoryProducts.get(
      recommendation.accessoryProductId
    );
    if (!product) {
      return [];
    }
    return [toOption(item, recommendation, product)];
  });

const exclusiveCategoryCode = (
  item: EquipmentAccessoryData
): string | undefined => {
  const codes = new Set(
    item.recommendations.map((recommendation) => recommendation.accessoryType)
  );
  if (codes.size !== 1) {
    return undefined;
  }
  const [code] = codes;
  if (!EXCLUSIVE_ACCESSORY_CATEGORIES.has(code)) {
    return undefined;
  }
  return code;
};

const keptRecommendedProductId = (
  group: AccessoryOptionGroupType
): number | undefined =>
  group.options.find((option) => option.isRecommended)?.product.productId;

const alternativeClearsForGroups = (
  item: EquipmentAccessoryData,
  groups: AccessoryOptionGroupType[]
): AccessoryQuantityChangeType[] =>
  groups.flatMap((group) => {
    const keptProductId = keptRecommendedProductId(group);
    if (group.mode !== 'single' || keptProductId === undefined) {
      return [];
    }
    return group.options.flatMap((option) => {
      if (option.product.productId === keptProductId || option.quantity < 1) {
        return [];
      }
      return [
        {
          condenserUniqueId: item.condenserUniqueId,
          equipmentUniqueId: item.equipmentUniqueId,
          accessory: option.product,
          quantity: 0
        }
      ];
    });
  });

export const humanizeAccessoryTypeCode = (code: string): string => {
  const override = ACCESSORY_TYPE_LABEL_OVERRIDES[code];
  if (override) {
    return override;
  }
  return code
    .split('_')
    .filter((word) => word.length > 0)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

export const buildAccessoryOptionGroups = (
  item: EquipmentAccessoryData,
  { quantitySelection }: AccessoryOptionGroupBuildOptionsType
): AccessoryOptionGroupType[] => {
  const exclusiveCode = exclusiveCategoryCode(item);
  if (exclusiveCode) {
    const options = optionsForItem(item).sort(byRecommendationPriority);
    if (options.length === 0) {
      return [];
    }
    return [
      {
        type: exclusiveCode,
        label: humanizeAccessoryTypeCode(exclusiveCode),
        mode: options.length > 1 ? 'single' : 'multi',
        options
      }
    ];
  }

  const optionsByType = new Map<string, AccessoryOptionType[]>();
  const typeOrder: string[] = [];

  item.recommendations.forEach((recommendation) => {
    const product = item.accessoryProducts.get(
      recommendation.accessoryProductId
    );
    if (!product) {
      return;
    }
    const option = toOption(item, recommendation, product);
    const existing = optionsByType.get(product.type);
    if (!existing) {
      optionsByType.set(product.type, [option]);
      typeOrder.push(product.type);
      return;
    }
    existing.push(option);
  });

  return typeOrder.map((type) => {
    const options = (optionsByType.get(type) ?? [])
      .slice()
      .sort(byRecommendationPriority);
    const recommendedCount = options.filter(
      (option) => option.isRecommended
    ).length;
    const singleChoice =
      !quantitySelection && options.length > 1 && recommendedCount <= 1;
    return {
      type,
      label: humanizeAccessoryTypeCode(type),
      mode: singleChoice ? 'single' : 'multi',
      options
    };
  });
};

export const buildRadioSelectionChanges = (
  group: AccessoryOptionGroupType,
  chosenProductId: number | null
): AccessorySelectionChangeType[] =>
  group.options.flatMap((option) => {
    const isChosen = option.product.productId === chosenProductId;
    const isSelected = option.quantity >= 1;
    if (!isChosen && !isSelected) {
      return [];
    }
    const nextQuantity = isChosen
      ? option.recommendation.recommendedQuantity ?? 1
      : 0;
    if (option.quantity === nextQuantity) {
      return [];
    }
    return [{ accessory: option.product, quantity: nextQuantity }];
  });

export const buildIncludeRecommendedChanges = ({
  equipment,
  include,
  includedAccessoryTypes,
  accessoryTypeCode,
  quantitySelection
}: {
  equipment: EquipmentAccessoryData[];
  include: boolean;
  includedAccessoryTypes?: Map<string, Set<string>>;
  accessoryTypeCode: string;
  quantitySelection: boolean;
}): AccessoryQuantityChangeType[] =>
  equipment.flatMap((item) => {
    const groups = buildAccessoryOptionGroups(item, { quantitySelection });
    const includedTypes = includedAccessoryTypes?.get(item.equipmentUniqueId);
    const lineSetSettings = getLineSetSettingsForEquipment(
      item,
      accessoryTypeCode
    );
    const recommendedChanges = groups.flatMap((group) => {
      const recommended = group.options.filter(
        (option) => option.isRecommended
      );
      const applicable =
        include && group.mode === 'single'
          ? recommended.slice(0, 1)
          : recommended;
      return applicable.flatMap((option) => {
        const includedWithLineSet = isAccessoryIncludedWithLineSet({
          accessoryType: option.product.type,
          lineSetSettings,
          includedTypes
        });
        if (includedWithLineSet) {
          return [];
        }
        return [
          {
            condenserUniqueId: item.condenserUniqueId,
            equipmentUniqueId: item.equipmentUniqueId,
            accessory: option.product,
            quantity: include
              ? option.recommendation.recommendedQuantity ?? 1
              : 0
          }
        ];
      });
    });
    if (!include) {
      return recommendedChanges;
    }
    return [...recommendedChanges, ...alternativeClearsForGroups(item, groups)];
  });

export const collectSingleChoiceAlternativesToClear = (
  panels: AccessoryPanelLikeType[]
): AccessoryQuantityChangeType[] =>
  panels.flatMap((panel) => {
    const quantitySelection = usesQuantitySelection(panel.accessoryType.code);
    return panel.equipment.flatMap((item) =>
      alternativeClearsForGroups(
        item,
        buildAccessoryOptionGroups(item, { quantitySelection })
      )
    );
  });
